import { ThemeProvider, styled } from "baseui";
import type { Theme } from "baseui/theme";
import { merge } from "lodash";
import type { ReactNode } from "react";
import React from "react";
import type { StyleObject } from "styletron-react";
import { EditLightTheme } from "../EditTheme";
import { CloseIcon } from "../icons/CloseIcon";
import { DocsInfoIcon } from "../icons/DocsInfoIcon";
import { FailureCircleIcon } from "../icons/FailureCircleIcon";
import { SuccessCircleIcon } from "../icons/SuccessCircleIcon";
import { WarningIcon } from "../icons/WarningIcon";
import { expandBorderRadii, paddingHorizontal, paddingUtil, paddingVertical } from "../utils";
import type { Overrides } from "../utils/overrides";
import { useOverrides } from "../utils/overrides";
import { Column } from "./Column";
import { Row } from "./Row";
import { IconButton } from "./button/IconButton";

export enum BannerKind {
  neutral,
  info,
  positive,
  warning,
  negative,
}

export const getStylesForBannerKind = <T extends Theme>($theme: T, kind?: BannerKind): StyleObject => {
  switch (kind) {
    case BannerKind.positive:
      return {
        backgroundColor: $theme.colors.green50,
        color: $theme.colors.green700,
      };
    case BannerKind.warning:
      return {
        backgroundColor: $theme.colors.yellow50,
        color: $theme.colors.yellow700,
      };
    case BannerKind.negative:
      return {
        backgroundColor: $theme.colors.red50,
        color: $theme.colors.red700,
      };
    case BannerKind.info:
      return {
        backgroundColor: $theme.colors.cyan50,
        color: $theme.colors.cyan700,
      };
    default:
      return {
        backgroundColor: $theme.colors.primary50,
        color: $theme.colors.primary700,
      };
  }
};

export const getIconForKind = <T extends Theme>($theme: T, kind?: BannerKind) => {
  switch (kind) {
    case BannerKind.info:
      return <DocsInfoIcon />;
    case BannerKind.positive:
      return <SuccessCircleIcon color={$theme.colors.green500} />;
    case BannerKind.warning:
      return <WarningIcon color={$theme.colors.yellow500} />;
    case BannerKind.negative:
      return <FailureCircleIcon color={$theme.colors.red500} />;
    default:
      return null;
  }
};

const baseThemeOverrides = { typography: { contentLink: { textDecoration: "underline" } } };

const BaseContentTheme = merge({}, EditLightTheme, baseThemeOverrides);
const InfoContentTheme = merge({}, EditLightTheme, baseThemeOverrides, {
  colors: {
    content: EditLightTheme.colors.cyan700,
    contentPrimary: EditLightTheme.colors.cyan700,
    contentLinkText: EditLightTheme.colors.cyan700,
    contentLinkHover: EditLightTheme.colors.cyan600,
    contentLinkActive: EditLightTheme.colors.cyan500,
  },
});

const WarningContentTheme = merge({}, EditLightTheme, baseThemeOverrides, {
  colors: {
    content: EditLightTheme.colors.yellow700,
    contentPrimary: EditLightTheme.colors.yellow700,
    contentLinkText: EditLightTheme.colors.yellow700,
    contentLinkHover: EditLightTheme.colors.yellow600,
    contentLinkActive: EditLightTheme.colors.yellow500,
  },
});

const NegativeContentTheme = merge({}, EditLightTheme, baseThemeOverrides, {
  colors: {
    content: EditLightTheme.colors.red700,
    contentPrimary: EditLightTheme.colors.red700,
    contentLinkText: EditLightTheme.colors.red700,
    contentLinkHover: EditLightTheme.colors.red600,
    contentLinkActive: EditLightTheme.colors.red500,
  },
});

const PositiveContentTheme = merge({}, EditLightTheme, baseThemeOverrides, {
  colors: {
    content: EditLightTheme.colors.green700,
    contentPrimary: EditLightTheme.colors.green700,
    contentLinkText: EditLightTheme.colors.green700,
    contentLinkHover: EditLightTheme.colors.green600,
    contentLinkActive: EditLightTheme.colors.green500,
  },
});

export const themeForBannerKind = (kind?: BannerKind) => {
  switch (kind) {
    case BannerKind.info:
      return InfoContentTheme;
    case BannerKind.positive:
      return PositiveContentTheme;
    case BannerKind.warning:
      return WarningContentTheme;
    case BannerKind.negative:
      return NegativeContentTheme;
    default:
      return BaseContentTheme;
  }
};

export const BannerTitle = styled("div", ({ $theme }) => ({
  ...$theme.typography.subHeading,
}));

export interface BannerProps {
  children?: ReactNode;
  title?: ReactNode;
  kind?: BannerKind;
  multiline?: boolean;
  $style?: StyleObject;
  noIcon?: boolean;
  onClose?: () => void;
  onCloseAlwaysEnabled?: boolean;
  footer?: ReactNode;
  "data-testid"?: string;
  overrides?: Overrides<{
    Title: typeof BannerTitle;
    CloseButton: typeof IconButton;
  }>;
}

export const Banner = (props: BannerProps) => {
  const { Title, CloseButton } = useOverrides(
    {
      Title: BannerTitle,
      CloseButton: IconButton,
    },
    props.overrides
  );

  const $theme = themeForBannerKind(props.kind);
  const icon = getIconForKind($theme, props.kind);

  const multiline = props.multiline || props.title || props.footer;

  const style = merge(
    {
      ...$theme.typography.LabelSmall,
      ...expandBorderRadii($theme.borders.surfaceBorderRadius),
      ...(multiline ? paddingUtil($theme.sizing.scale300) : paddingUtil($theme.sizing.scale200)),
      ...getStylesForBannerKind($theme, props.kind),
      gap: $theme.sizing.scale0,
    },
    props.$style
  );

  return (
    <ThemeProvider theme={$theme}>
      <Column $style={style} data-testid={props["data-testid"]}>
        <Row $style={{ gap: $theme.sizing.scale300, alignItems: multiline ? "start" : "top" }}>
          <Row
            $style={{
              ...paddingVertical($theme.sizing.scale0),
              ...paddingHorizontal($theme.sizing.scale100),
              gap: $theme.sizing.scale400,
              flex: 1,
              alignItems: "start",
            }}
          >
            {icon && !props.noIcon && <Column $style={{ ...paddingVertical($theme.sizing.scale0) }}>{icon}</Column>}
            {multiline ? props.title && <Title $style={{ flex: 1 }}>{props.title}</Title> : props.children && <div>{props.children}</div>}
          </Row>
          {!!props.onClose && (
            <CloseButton
              disabled={props.onCloseAlwaysEnabled ? false : undefined}
              onClick={props.onClose}
              label=""
              data-testid={props["data-testid"] ? `${props["data-testid"]}-close-button` : undefined}
            >
              <CloseIcon />
            </CloseButton>
          )}
        </Row>
        {multiline && (
          <Column
            $style={{
              paddingLeft: !props.kind ? $theme.sizing.scale100 : $theme.sizing.scale850,
              paddingRight: $theme.sizing.scale400,
              gap: $theme.sizing.scale0,
              flex: 1,
            }}
          >
            <div
              style={{
                ...paddingVertical($theme.sizing.scale100),
                paddingLeft: !props.kind ? 0 : $theme.sizing.scale0,
                gap: $theme.sizing.scale300,
                flex: 1,
                alignItems: "start",
              }}
            >
              {props.children}
            </div>
            {props.footer && <Row $style={{ ...paddingVertical($theme.sizing.scale100) }}>{props.footer}</Row>}
          </Column>
        )}
      </Column>
    </ThemeProvider>
  );
};
